// Deep Intel — analytics over the scorebook-PDF play-by-play (atBats docs).
//
// Everything here comes from data no box score shows: per-pitch sequences and
// per-PA game context. Sample sizes are youth-scale, so every result carries
// its sample and the UI copy stays suggestive (bench-coach voice), never a verdict.
//
// Pure + framework-free. Callers fetch the atBats docs (lazily — this can be
// thousands of docs across a season) and pass them in.

import type { AtBatDoc } from '../types/dugout';

// Order key for our pitchers' opposing PAs within a game:
// (timeThroughOrder − 1) × 9 + lineupSlot is monotonic across the game.
const paOrder = (ab: AtBatDoc) =>
  (ab.inning * 1000) + ((ab.timeThroughOrder ?? 1) - 1) * 9 + (ab.lineupSlot || 0);

const HIT_CODES = new Set(['1B', '2B', '3B', 'HR']);
const NON_AB_CODES = new Set(['BB', 'HBP', 'SAC', 'SF', 'CI']);

// ── Fatigue curve ────────────────────────────────────────────────────────────

export interface FatigueBucket {
  label: string;         // "1–20", "21–35", "36+"
  strikes: number;
  pitches: number;
  sPct: number | null;   // strikes / pitches
}

export interface PitcherFatigue {
  pitcherId: string;
  pitcherName: string;
  buckets: FatigueBucket[];
  totalPitches: number;
  games: number;
  /** true when the last bucket's S% drops ≥ 8 pts below the first's */
  fades: boolean;
}

const BUCKETS: Array<{ label: string; from: number; to: number }> = [
  { label: '1–20', from: 0, to: 20 },
  { label: '21–35', from: 20, to: 35 },
  { label: '36+', from: 35, to: Infinity },
];

/** S% by cumulative-pitch-count bucket for OUR pitchers. Min 60 total pitches. */
export function computeFatigueCurves(atBats: AtBatDoc[]): PitcherFatigue[] {
  const ours = atBats.filter(ab => ab.pitcherTeam === 'us' && ab.pitcherId && ab.pitchSequence?.length);

  const byPitcherGame = new Map<string, AtBatDoc[]>();
  ours.forEach(ab => {
    const k = `${ab.pitcherId}|${ab.gameId}`;
    if (!byPitcherGame.has(k)) byPitcherGame.set(k, []);
    byPitcherGame.get(k)!.push(ab);
  });

  const acc = new Map<string, { name: string; games: Set<string>; buckets: Array<{ s: number; p: number }> }>();

  byPitcherGame.forEach((pas, key) => {
    const [pid, gameId] = key.split('|');
    if (!acc.has(pid)) {
      acc.set(pid, { name: pas[0].pitcherName || '', games: new Set(), buckets: BUCKETS.map(() => ({ s: 0, p: 0 })) });
    }
    const a = acc.get(pid)!;
    a.games.add(gameId);

    let cum = 0;
    [...pas].sort((x, y) => paOrder(x) - paOrder(y)).forEach(ab => {
      ab.pitchSequence.forEach(pitch => {
        const bi = BUCKETS.findIndex(b => cum >= b.from && cum < b.to);
        if (bi >= 0) {
          a.buckets[bi].p++;
          if (pitch === 'S') a.buckets[bi].s++;
        }
        cum++;
      });
    });
  });

  const out: PitcherFatigue[] = [];
  acc.forEach((a, pid) => {
    const total = a.buckets.reduce((n, b) => n + b.p, 0);
    if (total < 60) return; // sample floor
    const buckets = a.buckets.map((b, i) => ({
      label: BUCKETS[i].label, strikes: b.s, pitches: b.p,
      sPct: b.p >= 10 ? b.s / b.p : null,
    }));
    const first = buckets[0].sPct;
    const last = [...buckets].reverse().find(b => b.sPct != null)?.sPct ?? null;
    out.push({
      pitcherId: pid, pitcherName: a.name, buckets,
      totalPitches: total, games: a.games.size,
      fades: first != null && last != null && first - last >= 0.08,
    });
  });
  return out.sort((a, b) => b.totalPitches - a.totalPitches);
}

// ── Times through the order ──────────────────────────────────────────────────

export interface TTOSplit {
  firstTime: { avg: number | null; ab: number; h: number };
  secondPlus: { avg: number | null; ab: number; h: number };
}

/** Opponent AVG vs our staff, 1st time through vs 2nd+. AB approximation excludes BB/HBP/SAC. */
export function computeTTOSplit(atBats: AtBatDoc[]): TTOSplit | null {
  const ours = atBats.filter(ab => ab.pitcherTeam === 'us' && ab.timeThroughOrder != null);
  if (ours.length === 0) return null;

  const tally = (pas: AtBatDoc[]) => {
    let ab = 0, h = 0;
    pas.forEach(pa => {
      if (NON_AB_CODES.has(pa.resultCode)) return;
      ab++;
      if (HIT_CODES.has(pa.resultCode)) h++;
    });
    return { ab, h, avg: ab >= 10 ? h / ab : null }; // sample floor per side
  };

  const first = tally(ours.filter(a => (a.timeThroughOrder ?? 1) <= 1));
  const second = tally(ours.filter(a => (a.timeThroughOrder ?? 1) >= 2));
  if (first.avg == null && second.avg == null) return null;
  return { firstTime: first, secondPlus: second };
}

// ── Leadoff rally math ───────────────────────────────────────────────────────

export interface RallyMath {
  leadoffOn: { innings: number; runs: number; runsPerInning: number };
  leadoffOut: { innings: number; runs: number; runsPerInning: number };
}

/** OUR offensive innings: runs per inning when the leadoff man reaches vs not. */
export function computeRallyMath(atBats: AtBatDoc[]): RallyMath | null {
  const ours = atBats.filter(ab => ab.batterTeam === 'us');
  const byInning = new Map<string, AtBatDoc[]>();
  ours.forEach(ab => {
    const k = `${ab.gameId}|${ab.inning}`;
    if (!byInning.has(k)) byInning.set(k, []);
    byInning.get(k)!.push(ab);
  });

  const on = { innings: 0, runs: 0 };
  const out = { innings: 0, runs: 0 };
  byInning.forEach(pas => {
    const leadoff = pas.find(p => p.isLeadoff);
    if (!leadoff) return; // can't classify the inning
    const runs = pas.reduce((n, p) => n + (p.runnersScored || 0), 0);
    const side = leadoff.reachedBase ? on : out;
    side.innings++;
    side.runs += runs;
  });

  if (on.innings < 5 || out.innings < 5) return null; // sample floor per side
  return {
    leadoffOn: { ...on, runsPerInning: on.runs / on.innings },
    leadoffOut: { ...out, runsPerInning: out.runs / out.innings },
  };
}
